import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  formatPrice,
  siteConfig,
} from "../Datas/productData";
import "../css/cartpage.scss";

const getColorLabel = (color) => {
  if (!color) return "Default";
  return typeof color === "string" ? color : color.name || "Default";
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart, removeFromCart, updateQuantity } =
    useContext(CartContext);

  const lineItems = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find(({ id }) => id === item.id);
          return product ? { ...item, product } : null;
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      lineItems.reduce(
        (total, item) => total + item.product.price * item.quantity,
        0
      ),
    [lineItems]
  );

  const itemCount = lineItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const freeThreshold = siteConfig.shipping.freeThreshold;
  const standardDelivery = siteConfig.shipping.standard;
  const freeDeliveryRemaining = Math.max(freeThreshold - subtotal, 0);
  const deliveryCost = freeDeliveryRemaining === 0 ? 0 : standardDelivery;
  const total = subtotal + deliveryCost;
  const shippingProgress = Math.min((subtotal / freeThreshold) * 100, 100);

  if (lineItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <nav className="cart-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span>Shopping bag</span>
        </nav>
        <section className="cart-empty" aria-labelledby="empty-cart-title">
          <span className="cart-empty__eyebrow">Your shopping bag</span>
          <h1 id="empty-cart-title">Your bag is empty</h1>
          <p>
            Discover considered leather bags and everyday accessories made for
            the rhythm of the city.
          </p>
          <Link className="cart-primary-link" to="/shop">
            Explore the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-page__container">
        <nav className="cart-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span>Shopping bag</span>
        </nav>

        <div className="cart-page__heading">
          <div>
            <span className="cart-page__eyebrow">Basket</span>
            <h1>Your shopping bag</h1>
            <p>
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </p>
          </div>
          <button className="cart-text-button" type="button" onClick={clearCart}>
            Clear bag
          </button>
        </div>

        <div className="cart-layout">
          <section className="cart-lines" aria-label="Products in your bag">
            {lineItems.map((item) => {
              const { product } = item;
              const colorLabel = getColorLabel(item.color);
              const image = product.images?.[0] || product.image;
              const itemKey = `${item.id}-${item.size || "one-size"}-${colorLabel}`;

              return (
                <article className="cart-line" key={itemKey}>
                  <Link
                    className="cart-line__image"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    <img src={image} alt={product.imageAlt || product.name} />
                    {product.status && (
                      <span className="cart-line__badge">{product.status}</span>
                    )}
                  </Link>

                  <div className="cart-line__content">
                    <div className="cart-line__top">
                      <div>
                        <p className="cart-line__category">
                          {product.category}
                        </p>
                        <h2>
                          <Link to={`/shopDetails/${product.id}`}>
                            {product.name}
                          </Link>
                        </h2>
                      </div>
                      <p className="cart-line__total">
                        {formatPrice(product.price * item.quantity)}
                      </p>
                    </div>

                    <dl className="cart-line__meta">
                      <div>
                        <dt>Color</dt>
                        <dd>{colorLabel}</dd>
                      </div>
                      <div>
                        <dt>Size</dt>
                        <dd>{item.size || "One size"}</dd>
                      </div>
                    </dl>

                    <div className="cart-line__footer">
                      <div className="cart-quantity" aria-label="Quantity">
                        <button
                          type="button"
                          disabled={item.quantity <= 1}
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity - 1
                            )
                          }
                          aria-label={`Decrease ${product.name} quantity`}
                        >
                          −
                        </button>
                        <span aria-live="polite">{item.quantity}</span>
                        <button
                          type="button"
                          disabled={item.quantity >= 10}
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity + 1
                            )
                          }
                          aria-label={`Increase ${product.name} quantity`}
                        >
                          +
                        </button>
                      </div>

                      <div className="cart-line__actions">
                        <span>{formatPrice(product.price)} each</span>
                        <button
                          type="button"
                          onClick={() =>
                            removeFromCart(item.id, item.size, item.color)
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            <Link className="cart-continue-link" to="/shop">
              <span aria-hidden="true">←</span> Continue shopping
            </Link>
          </section>

          <aside className="cart-summary" aria-labelledby="order-summary-title">
            <div className="cart-summary__inner">
              <h2 id="order-summary-title">Order summary</h2>

              <div className="cart-shipping-progress">
                <p>
                  {freeDeliveryRemaining > 0
                    ? `Add ${formatPrice(
                        freeDeliveryRemaining
                      )} for free standard delivery.`
                    : "You qualify for free standard delivery."}
                </p>
                <div
                  className="cart-shipping-progress__track"
                  role="progressbar"
                  aria-label="Free delivery progress"
                  aria-valuemin="0"
                  aria-valuemax={freeThreshold}
                  aria-valuenow={Math.min(subtotal, freeThreshold)}
                >
                  <span style={{ width: `${shippingProgress}%` }} />
                </div>
              </div>

              <dl className="cart-summary__rows">
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatPrice(subtotal)}</dd>
                </div>
                <div>
                  <dt>Standard delivery</dt>
                  <dd>{deliveryCost === 0 ? "Free" : formatPrice(deliveryCost)}</dd>
                </div>
                <div className="cart-summary__total">
                  <dt>Total</dt>
                  <dd>{formatPrice(total)}</dd>
                </div>
              </dl>

              <p className="cart-summary__tax-note">
                VAT included. Delivery is calculated from the current bag
                value.
              </p>

              <button
                className="cart-checkout-button"
                type="button"
                onClick={() => navigate("/checkout")}
              >
                Proceed to checkout
              </button>

              <ul className="cart-summary__assurances" aria-label="Store assurances">
                <li>Secure guest checkout</li>
                <li>{siteConfig.shipping.returnDays}-day returns</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CartPage;
